/**
 * SEO Metadata Configuration
 * Centralized metadata management for SEO and LLM discoverability
 */

const DEFAULT_SITE_URL = "https://evangelist.so";

function normalizeSiteUrl(rawValue: string | undefined): string | null {
  if (!rawValue) {
    return null;
  }

  const trimmed = rawValue.trim();
  if (!trimmed) {
    return null;
  }

  const candidates = /^https?:\/\//i.test(trimmed) ? [trimmed] : [`https://${trimmed}`, `http://${trimmed}`];

  for (const candidate of candidates) {
    try {
      const url = new URL(candidate);
      return url.origin;
    } catch {
      // Ignore invalid URLs and try the next candidate.
      continue;
    }
  }

  return null;
}

function resolveSiteUrl() {
  const importMetaEnv = typeof import.meta !== "undefined" ? import.meta.env : undefined;
  const globalProcessEnv =
    typeof globalThis !== "undefined"
      ? ((globalThis as { process?: { env?: Record<string, unknown> } }).process?.env ?? undefined)
      : undefined;
  const envCandidates = [
    importMetaEnv?.VITE_PUBLIC_SITE_URL,
    globalProcessEnv?.VITE_PUBLIC_SITE_URL as string | undefined,
  ];

  for (const candidate of envCandidates) {
    const normalized = normalizeSiteUrl(candidate);
    if (normalized) {
      return normalized;
    }
  }

  return DEFAULT_SITE_URL;
}

const SITE_URL = resolveSiteUrl();

export const siteConfig = {
  name: "Evangelist",
  title: "Evangelist - Programmatic Short-Form Clipping Exchange",
  description:
    "Enterprise-grade exchange connecting media buyers with verified clipping networks across Shorts, TikTok, and Reels. Brand safety and zkTLS verification.",
  url: SITE_URL,
  ogImage: "/og-image.png",
  keywords: [
    // Core business - B2B programmatic exchange
    "programmatic video exchange",
    "short-form video advertising",
    "enterprise video exchange",
    "creator content marketplace",
    "verified video inventory",

    // Brand/Agency focused (primary audience)
    "buy short-form video ads",
    "enterprise media buying",
    "YouTube Shorts advertising",
    "TikTok advertising platform",
    "Instagram Reels advertising",
    "brand safety verification",
    "video ad exchange",
    "creator content buying",

    // Clipping Platform Partners (supply side)
    "clipping platform partnerships",
    "creator network monetization",
    "Discord clipping communities",
    "Fixated partnership",
    "SuperClip community",
    "verified creator networks",

    // Technical differentiators
    "zkTLS verification",
    "multimodal AI screening",
    "anti-fraud verification",
    "brand safety AI",
    "creator data verification",
    "fraud prevention video ads",
    "transparent video metrics",

    // Platform integrations
    "YouTube Shorts campaigns",
    "TikTok creator content",
    "Instagram Reels campaigns",
    "multi-platform video distribution",

    // Use cases
    "enterprise video campaigns",
    "verified views",
    "brand-safe content",
    "tier-1 brand campaigns",
    "advertiser demand platform",
  ],
  authors: [{ name: "Evangelist" }],
  creator: "Evangelist",
  publisher: "Evangelist",
  category: "Technology",
  classification: "AdTech Platform",
  contact: {
    sales: "sales@evangelist.so",
    support: "support@evangelist.so",
    calendly: "https://calendly.com/dusan-evangelist/30min",
  },
  /**
   * The company's own profiles, in one place: they are the `sameAs` that ties this domain to the
   * real-world organization, and the profile URL `article:author` is defined to carry. Declaring
   * them here rather than in each consumer is what stops the schema and the meta tags naming
   * different accounts.
   */
  socialProfiles: {
    linkedin: "https://www.linkedin.com/company/evangelisthq",
    x: "https://x.com/EvangelistHQ",
  },
};

/**
 * Twitter card tags. `site` and `creator` are the real @EvangelistHQ account — the previous
 * `@evangelist` value belongs to an unrelated profile, so every share credited the wrong one.
 */
type TwitterMetadata = {
  card: string;
  title: string;
  description: string;
  images: string[];
  site?: string;
  creator?: string;
};

const twitterMetadata: TwitterMetadata = {
  card: "summary_large_image",
  title: siteConfig.title,
  description: siteConfig.description,
  images: [siteConfig.ogImage],
  site: "@EvangelistHQ",
  creator: "@EvangelistHQ",
};

export const baseMetadata = {
  metadataBase: new URL(siteConfig.url),
  title: {
    default: siteConfig.title,
    template: `%s | ${siteConfig.name}`,
  },
  description: siteConfig.description,
  keywords: siteConfig.keywords,
  authors: siteConfig.authors,
  creator: siteConfig.creator,
  publisher: siteConfig.publisher,
  formatDetection: {
    email: false,
    address: false,
    telephone: false,
  },
  openGraph: {
    type: "website",
    locale: "en_US",
    url: siteConfig.url,
    title: siteConfig.title,
    description: siteConfig.description,
    siteName: siteConfig.name,
    images: [
      {
        url: siteConfig.ogImage,
        width: 1200,
        height: 630,
        alt: `${siteConfig.name} - Professional Video Clipping Platform`,
        type: "image/png",
      },
    ],
  },
  twitter: twitterMetadata,
  robots: {
    index: true,
    follow: true,
    googleBot: {
      index: true,
      follow: true,
      "max-video-preview": -1,
      "max-image-preview": "large",
      "max-snippet": -1,
    },
  },
  verification: {},
  alternates: {
    canonical: siteConfig.url,
  },
  category: siteConfig.category,
};

/**
 * Format a page title with the shared site template (`%s | Evangelist`), falling
 * back to the default site title when none is given. Used by `<PageTitle>` so
 * admin/creator/launch tab titles stay consistent with the `<SeoHead>` pages.
 */
export function formatPageTitle(title?: string): string {
  const trimmed = title?.trim();
  if (!trimmed) {
    return siteConfig.title;
  }
  return baseMetadata.title.template.replace("%s", trimmed);
}

/**
 * Open Graph article metadata. A page that passes this is published as `og:type=article` and
 * carries the `article:*` tags, which is what makes a crawler or a share card treat it as a
 * dated piece of writing rather than another marketing page — without it, an article is
 * indistinguishable from the site's landing pages in the graph.
 */
type ArticleMetadata = {
  /** ISO 8601 (`YYYY-MM-DD` or a full timestamp). */
  publishedTime: string;
  modifiedTime: string;
  /** The section the article belongs to, e.g. "Fraud prevention". */
  section?: string;
  tags?: string[];
};

/**
 * Generate page-specific metadata
 */
export function generatePageMetadata({
  title,
  description,
  path = "",
  image,
  noIndex = false,
  article,
}: {
  title?: string;
  description?: string;
  path?: string;
  image?: string;
  noIndex?: boolean;
  article?: ArticleMetadata;
}) {
  const baseUrl = siteConfig.url.replace(/\/+$/, "");
  const normalizedPath = path ? (path.startsWith("/") ? path : `/${path}`) : "";
  const url = `${baseUrl}${normalizedPath}`;
  const ogImage = image || siteConfig.ogImage;

  return {
    ...baseMetadata,
    title: title || baseMetadata.title,
    description: description || siteConfig.description,
    openGraph: {
      ...baseMetadata.openGraph,
      url,
      type: article ? "article" : baseMetadata.openGraph.type,
      article,
      title: title || siteConfig.title,
      description: description || siteConfig.description,
      images: [
        {
          url: ogImage,
          width: 1200,
          height: 630,
          alt: title || siteConfig.name,
        },
      ],
    },
    twitter: {
      ...baseMetadata.twitter,
      title: title || siteConfig.title,
      description: description || siteConfig.description,
      images: [ogImage],
    },
    alternates: {
      canonical: url,
    },
    robots: noIndex
      ? {
          index: false,
          follow: false,
        }
      : baseMetadata.robots,
  };
}
